// src/components/AssetDistribution.js
import React, { memo } from 'react';
import styled from 'styled-components/macro';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { messages } from './messages';
import { media } from 'styles/media';

import { buildStyles, CircularProgressbar } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { useNetworth } from 'app/Provider/NetworthProvider';

import { CommoditiesSvg } from 'assets/assets_page/assetDistribution/CommoditiesSvg';
import { MutualFundsSvg } from 'assets/assets_page/assetDistribution/MutualFundsSvg';
import { GoldSvg } from 'assets/assets_page/assetDistribution/GoldSvg';
import { FixedDepositSvg } from 'assets/assets_page/assetDistribution/FixedDepositSvg';
import { StocksSvg } from 'assets/assets_page/assetDistribution/StocksSvg';
import { SavingsSvg } from 'assets/assets_page/assetDistribution/SavingsSvg';
import { CryptoCurrencySvg } from 'assets/assets_page/assetDistribution/CryptoCurrencySvg';
import { RealEstateSvg } from 'assets/assets_page/assetDistribution/RealEsatate';
import { ForexSvg } from 'assets/assets_page/assetDistribution/ForexSvg';
import { OthersSvg } from 'assets/assets_page/assetDistribution/OthersSvg';
import { Track } from 'constants/constants';
import { Button } from '../Button/Loadable';

interface Props {}

export const AssetDistribution = memo((props: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login } = useNetworth();

  const strokeColor = login ? '#AF8EFF' : '#C8CCD8';

  return (
    <Div className="border  position-relative rounded-4  bg-white">
      {t('')}
      <div className="d-flex justify-content-between p-3 align-items-center border-bottom">
        <Heading>{t(messages.name())}</Heading>
        <img src={Track} alt="" />
      </div>
      <div className="row row-cols-xl-6 row-cols-2 row-cols-md-3 row-gap-5 p-2">
        <div className="col">
          <Container
            name={t(messages.stocks())}
            price={'3,000.00'}
            percentage={5}
            children={<StocksSvg stroke={strokeColor} />}
          />
        </div>
        <div className="col">
          <Container
            name={t(messages.mutual())}
            price={'10,000.00'}
            percentage={10}
            children={<MutualFundsSvg stroke={strokeColor} />}
          />
        </div>
        <div className="col">
          <Container
            name={t(messages.saving())}
            price={'50,000.00'}
            percentage={75}
            children={<SavingsSvg stroke={strokeColor} />}
          />
        </div>
        <div className="col">
          <Container
            name={t(messages.gold())}
            price={'3,000.00'}
            percentage={3}
            children={<GoldSvg stroke={strokeColor} />}
          />
        </div>
        <div className="col">
          <Container
            name={t(messages.fixed())}
            price={'0.00'}
            percentage={0}
            children={<FixedDepositSvg stroke={strokeColor} />}
          />
        </div>
        <div className="col">
          <Container
            name={t(messages.commodity())}
            price={'0.00'}
            percentage={0}
            children={<CommoditiesSvg stroke={strokeColor} />}
          />
        </div>
        <div className="col">
          <Container
            name={t(messages.cryptocurrency())}
            price={'0.00'}
            percentage={0}
            children={<CryptoCurrencySvg stroke={strokeColor} />}
          />
        </div>
        <div className="col">
          <Container
            name={t(messages.real())}
            price={'0.00'}
            percentage={0}
            children={<RealEstateSvg stroke={strokeColor} />}
          />
        </div>
        <div className="col">
          <Container
            name={t(messages.forex())}
            price={'0.00'}
            percentage={0}
            children={<ForexSvg stroke={strokeColor} />}
          />
        </div>
        <div className="col">
          <Container
            name={t(messages.others())}
            price={'0.00'}
            percentage={0}
            children={<OthersSvg stroke={strokeColor} />}
          />
        </div>
      </div>
      <ButtonWrapper className="d-flex gap-3 justify-content-center p-3  bottom-0 end-0  ">
        <CustomButton
          className={` bg-primary d-flex align-items-center justify-content-center  ${
            login ? 'btn-primary' : 'btn-secondary disabled'
          }`}
          onClick={() => {
            navigate('/asset-overview');
          }}
        >
          Allocate asset-goal
        </CustomButton>
        <Button
          variant="#8276F4"
          onClick={() => {
            navigate('/addasset');
          }}
        >
          Add assets
        </Button>
      </ButtonWrapper>
    </Div>
  );
});
const CustomButton = styled.div`
  width: 203px;
  height: 52px;
  border-radius: 30px;
  font-size: 16px;
  font-weight: 800;
`;
interface CircularProgressWithImageProps {
  percentage: number;

  children: React.ReactNode;
}

interface ContainerProps {
  name: string;
  price: string;
  percentage: number;
  children: React.ReactNode;
}

const CircularProgressWithImage: React.FC<CircularProgressWithImageProps> = ({
  percentage,
  children,
}) => {
  return (
    <Circle className="progress-container d-flex align-items-center justify-content-center position-relative">
      <CircularProgressbar
        value={percentage}
        styles={buildStyles({
          pathColor: '#6c63ff',
          trailColor: '#d6d6d6',
        })}
      />
      <div className="position-absolute">{children}</div>
    </Circle>
  );
};
const Container: React.FC<ContainerProps> = ({
  name,
  price,
  percentage,
  children,
}) => {
  const { login } = useNetworth();
  const displayPrice = login ? price : '0.00';
  const displayPercentage = login ? percentage : 0;
  return (
    <div className="d-flex align-items-center gap-3">
      <CircularProgressWithImage
        percentage={displayPercentage}
        children={children}
      />
      <div className="d-flex flex-column">
        <Name className="my-2">{name}</Name>
        <Price className="">₹ {displayPrice}</Price>
      </div>
    </div>
  );
};

const Div = styled.div`
  width: auto;
  height: 254px;
  overflow: hidden;
  ${media.medium} {
    width: 70vw;
    height: auto;
  }
  ${media.small} {
    width: 90vw;
    height: auto;
  }
`;

const Circle = styled.div`
  height: 50px;
  width: 50px;
  position: relative;
`;

const Heading = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #22283a;
  ${media.medium} {
    font-size: 14px;
  }
`;

const Name = styled.h3`
  font-size: 10px;
  font-weight: 400;
`;

const Price = styled.h4`
  font-size: 1vw;
  font-weight: 800;
  ${media.medium} {
    font-size: 13px;
  }
`;

const ButtonWrapper = styled.div`
  background-color: #f5f6fa;
  border-radius: 25px;

  position: absolute;
  ${media.small} {
    position: static;
    background-color: white;
  }
`;
